/**
 * The project a draft is pointed at, as the composer presents it.
 *
 * Extracted from `NewThreadLanding.logic.ts` so the trigger's identity rules
 * sit next to the resolvers they consume instead of inside a file the size
 * gate had already capped. Two joins are in play, both shared with the sidebar
 * row: the name a person chose lives on the linked CMMD record, and a
 * checkout's own label is only a fallback.
 */
import type { EnvironmentId } from "@cmmd-forge/contracts";
import { projectScriptCwd } from "@cmmd-forge/shared/projectScripts";
import type { Project } from "../../types";
import {
  deriveProjectDisplayName,
  projectSelectionKey,
  type ProjectSummary,
} from "./NewThreadLanding.logic";

export interface ActiveProjectPresentation {
  readonly key: string | null;
  readonly name: string | null;
  readonly gitCwd: string | null;
  readonly summary: ProjectSummary | null;
  readonly environmentId: EnvironmentId | null;
}

/**
 * What the connected-project join contributes to the trigger.
 *
 * `resolveDisplayName` is the same display-name join the sidebar rows use: a
 * checkout imported from CMMD is named after its directory, and the name a
 * person chose lives on the linked CMMD record. Without it the hero sentence
 * says "What should we build in cmmd?" while the sidebar says "CMMD".
 */
export interface ActiveProjectPresentationResolvers {
  readonly resolveDisplayName?:
    | ((environmentId: string, projectId: string) => string | undefined)
    | undefined;
}

export function resolveActiveProjectPresentation(
  activeProject: Project | null,
  activeCloudSummary: ProjectSummary | null,
  resolvers: ActiveProjectPresentationResolvers = {},
): ActiveProjectPresentation {
  if (activeProject === null) {
    return {
      key: activeCloudSummary ? projectSelectionKey(activeCloudSummary) : null,
      name: activeCloudSummary?.name ?? null,
      gitCwd: null,
      summary: activeCloudSummary,
      environmentId: activeCloudSummary
        ? (activeCloudSummary.environmentId as EnvironmentId)
        : null,
    };
  }

  const name =
    resolvers.resolveDisplayName?.(activeProject.environmentId, activeProject.id) ??
    deriveProjectDisplayName(activeProject);
  return {
    key: projectSelectionKey(activeProject),
    name,
    gitCwd: projectScriptCwd({ project: { cwd: activeProject.cwd }, worktreePath: null }),
    summary: {
      id: activeProject.id,
      environmentId: activeProject.environmentId,
      name,
      // The checkout path, so the chip's mark can reach the favicon ladder
      // when no CMMD identity answers for this project.
      cwd: activeProject.cwd,
    },
    environmentId: activeProject.environmentId,
  };
}
